import { useEffect, useState } from 'react'
import { UploadOutlined } from '@ant-design/icons'
import {
  Button,
  DatePicker,
  Form,
  Input,
  message,
  Modal,
  Radio,
  Upload,
} from 'antd'
import { RcFile, UploadFile } from 'antd/lib/upload/interface'
import { UploadRequestOption } from 'rc-upload/lib/interface'
import moment, { Moment } from 'moment'
import {
  SUBMIT_OVERDUE_FEEDBACK,
  UPLOAD_OVERDUE_FEEDBACK_FILE,
  OverdueFeedbackUploadedFile,
} from '@/api/orderManage'
import styles from './style.module.scss'

type FormValues = {
  feedbackType: '1' | '2'
  expectFinishTime: Moment
  reason: string
}

type Props = {
  open: boolean
  orderId: string
  onCancel: () => void
  onSuccess: () => void
}

const getErrorMessage = (error: unknown, fallback: string) => {
  return error instanceof Error && error.message ? error.message : fallback
}

const hasErrorFields = (
  error: unknown,
): error is {
  errorFields: Array<{ name: Array<string | number>; errors: string[] }>
} => {
  return typeof error === 'object' && error !== null && 'errorFields' in error
}

export default function OverdueFeedbackModal({
  open,
  orderId,
  onCancel,
  onSuccess,
}: Props) {
  const [form] = Form.useForm<FormValues>()
  const [fileList, setFileList] = useState<UploadFile[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [reasonLength, setReasonLength] = useState(0)

  useEffect(() => {
    if (!open) {
      form.resetFields()
      setFileList([])
      setReasonLength(0)
    }
  }, [form, open])

  const validateFile = (file: RcFile, uploadBatch: RcFile[]) => {
    const extension = file.name.split('.').pop()?.toLowerCase()
    if (!['png', 'jpg', 'jpeg', 'pdf'].includes(extension ?? '')) {
      message.warning('凭证仅支持png、jpg、pdf格式')
      return Upload.LIST_IGNORE
    }
    if (file.size > 5 * 1024 * 1024) {
      message.warning('单个凭证不能超过5MB')
      return Upload.LIST_IGNORE
    }
    const batchIndex = Math.max(
      uploadBatch.findIndex((item) => item.uid === file.uid),
      0,
    )
    if (fileList.length + batchIndex >= 10) {
      message.warning('凭证最多上传10个')
      return Upload.LIST_IGNORE
    }
    return true
  }

  const uploadFile = async (options: UploadRequestOption) => {
    try {
      const response = await UPLOAD_OVERDUE_FEEDBACK_FILE(
        { code: orderId },
        options.file as RcFile,
      )
      if (response.code !== 0 || !response.data?.[0]) {
        throw new Error(response.msg || '上传失败')
      }
      options.onSuccess?.(response.data[0])
    } catch (error: unknown) {
      const uploadError = error instanceof Error ? error : new Error('上传失败')
      message.warning(getErrorMessage(uploadError, '上传失败'))
      options.onError?.(uploadError)
    }
  }

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      if (fileList.some((file) => file.status === 'uploading')) {
        message.warning('凭证正在上传，请稍后提交')
        return
      }
      if (fileList.some((file) => file.status === 'error' || !file.response)) {
        message.warning('存在上传失败的凭证，请删除后重试')
        return
      }
      setSubmitting(true)
      const response = await SUBMIT_OVERDUE_FEEDBACK({
        orderId,
        feedbackType: values.feedbackType,
        expectFinishTime: values.expectFinishTime.format('YYYY-MM-DD'),
        reason: values.reason,
        attachments: fileList.map((file) => {
          const uploaded = file.response as
            | OverdueFeedbackUploadedFile
            | undefined
          return {
            name: uploaded?.name || file.name,
            url: `${uploaded?.baseUrl || ''}${uploaded?.url || file.url || ''}`,
          }
        }),
      })
      if (response.code !== 0) {
        message.warning(response.msg)
        return
      }
      const label = values.feedbackType === '1' ? '发货超期' : '妥投超期'
      message.success(`已提交〔${label}〕反馈`)
      form.resetFields()
      setFileList([])
      setReasonLength(0)
      onSuccess()
    } catch (error: unknown) {
      if (hasErrorFields(error)) {
        const reasonTooLong = error.errorFields.some(
          (field) =>
            field.name.includes('reason') &&
            field.errors.includes('反馈原因不能超过500字'),
        )
        message.warning(
          reasonTooLong ? '反馈原因不能超过500字' : '请填写必填项',
        )
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      title="订单超期反馈"
      open={open}
      onCancel={onCancel}
      onOk={handleSubmit}
      confirmLoading={submitting}
      okText="提交"
      cancelText="取消"
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="feedbackType"
          label="反馈类型"
          rules={[{ required: true, message: '请选择反馈类型' }]}
        >
          <Radio.Group>
            <Radio value="1">发货超期</Radio>
            <Radio value="2">妥投超期</Radio>
          </Radio.Group>
        </Form.Item>
        <Form.Item
          name="expectFinishTime"
          label="预计完成时间"
          rules={[{ required: true, message: '请选择预计完成时间' }]}
        >
          <DatePicker
            style={{ width: '100%' }}
            disabledDate={(date) =>
              Boolean(date && date.isBefore(moment().startOf('day')))
            }
          />
        </Form.Item>
        <Form.Item
          name="reason"
          label="反馈原因"
          extra={
            <span
              className={
                reasonLength > 500 ? styles.overLimit : styles.reasonCount
              }
            >
              {reasonLength}/500
            </span>
          }
          rules={[
            { required: true, message: '请输入反馈原因' },
            { max: 500, message: '反馈原因不能超过500字' },
          ]}
        >
          <Input.TextArea
            rows={4}
            onChange={(event) => setReasonLength(event.target.value.length)}
          />
        </Form.Item>
        <Form.Item label="凭证">
          <Upload
            fileList={fileList}
            accept=".png,.jpg,.jpeg,.pdf"
            multiple
            maxCount={10}
            beforeUpload={validateFile}
            customRequest={uploadFile}
            onChange={({ fileList: next }) => setFileList(next)}
            onPreview={(file) => {
              const uploaded = file.response as
                | OverdueFeedbackUploadedFile
                | undefined
              const url = `${uploaded?.baseUrl || ''}${uploaded?.url || file.url || ''}`
              if (url) window.open(url, '_blank', 'noopener,noreferrer')
            }}
          >
            <Button icon={<UploadOutlined />}>上传凭证</Button>
          </Upload>
        </Form.Item>
      </Form>
    </Modal>
  )
}
import { useEffect, useState } from 'react'
import { UploadOutlined } from '@ant-design/icons'
import {
  Button,
  DatePicker,
  Form,
  Input,
  message,
  Modal,
  Radio,
  Upload,
} from 'antd'
import { RcFile, UploadFile } from 'antd/lib/upload/interface'
import { UploadRequestOption } from 'rc-upload/lib/interface'
import moment, { Moment } from 'moment'
import {
  SUBMIT_OVERDUE_FEEDBACK,
  UPLOAD_OVERDUE_FEEDBACK_FILE,
  OverdueFeedbackUploadedFile,
} from '@/api/orderManage'
import styles from './style.module.scss'

type FormValues = {
  feedbackType: '1' | '2'
  expectFinishTime: Moment
  reason: string
}

type Props = {
  open: boolean
  orderId: string
  onCancel: () => void
  onSuccess: () => void
}

const getErrorMessage = (error: unknown, fallback: string) => {
  return error instanceof Error && error.message ? error.message : fallback
}

const hasErrorFields = (
  error: unknown,
): error is {
  errorFields: Array<{ name: Array<string | number>; errors: string[] }>
} => {
  return typeof error === 'object' && error !== null && 'errorFields' in error
}

export default function OverdueFeedbackModal({
  open,
  orderId,
  onCancel,
  onSuccess,
}: Props) {
  const [form] = Form.useForm<FormValues>()
  const [fileList, setFileList] = useState<UploadFile[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [reasonLength, setReasonLength] = useState(0)

  useEffect(() => {
    if (!open) {
      form.resetFields()
      setFileList([])
      setReasonLength(0)
    }
  }, [form, open])

  const validateFile = (file: RcFile, uploadBatch: RcFile[]) => {
    const extension = file.name.split('.').pop()?.toLowerCase()
    if (!['png', 'jpg', 'jpeg', 'pdf'].includes(extension ?? '')) {
      message.warning('凭证仅支持png、jpg、pdf格式')
      return Upload.LIST_IGNORE
    }
    if (file.size > 5 * 1024 * 1024) {
      message.warning('单个凭证不能超过5MB')
      return Upload.LIST_IGNORE
    }
    const batchIndex = Math.max(
      uploadBatch.findIndex((item) => item.uid === file.uid),
      0,
    )
    if (fileList.length + batchIndex >= 10) {
      message.warning('凭证最多上传10个')
      return Upload.LIST_IGNORE
    }
    return true
  }

  const uploadFile = async (options: UploadRequestOption) => {
    try {
      const response = await UPLOAD_OVERDUE_FEEDBACK_FILE(
        { code: orderId },
        options.file as RcFile,
      )
      if (response.code !== 0 || !response.data?.[0]) {
        throw new Error(response.msg || '上传失败')
      }
      options.onSuccess?.(response.data[0])
    } catch (error: unknown) {
      const uploadError = error instanceof Error ? error : new Error('上传失败')
      message.warning(getErrorMessage(uploadError, '上传失败'))
      options.onError?.(uploadError)
    }
  }

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      if (fileList.some((file) => file.status === 'uploading')) {
        message.warning('凭证正在上传，请稍后提交')
        return
      }
      if (fileList.some((file) => file.status === 'error' || !file.response)) {
        message.warning('存在上传失败的凭证，请删除后重试')
        return
      }
      setSubmitting(true)
      const response = await SUBMIT_OVERDUE_FEEDBACK({
        orderId,
        feedbackType: values.feedbackType,
        expectFinishTime: values.expectFinishTime.format('YYYY-MM-DD'),
        reason: values.reason,
        attachments: fileList.map((file) => {
          const uploaded = file.response as
            | OverdueFeedbackUploadedFile
            | undefined
          return {
            name: uploaded?.name || file.name,
            url: `${uploaded?.baseUrl || ''}${uploaded?.url || file.url || ''}`,
          }
        }),
      })
      if (response.code !== 0) {
        message.warning(response.msg)
        return
      }
      const label = values.feedbackType === '1' ? '发货超期' : '妥投超期'
      message.success(`已提交〔${label}〕反馈`)
      form.resetFields()
      setFileList([])
      setReasonLength(0)
      onSuccess()
    } catch (error: unknown) {
      if (hasErrorFields(error)) {
        const reasonTooLong = error.errorFields.some(
          (field) =>
            field.name.includes('reason') &&
            field.errors.includes('反馈原因不能超过500字'),
        )
        message.warning(
          reasonTooLong ? '反馈原因不能超过500字' : '请填写必填项',
        )
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      title="订单超期反馈"
      open={open}
      onCancel={onCancel}
      onOk={handleSubmit}
      confirmLoading={submitting}
      okText="提交"
      cancelText="取消"
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="feedbackType"
          label="反馈类型"
          rules={[{ required: true, message: '请选择反馈类型' }]}
        >
          <Radio.Group>
            <Radio value="1">发货超期</Radio>
            <Radio value="2">妥投超期</Radio>
          </Radio.Group>
        </Form.Item>
        <Form.Item
          name="expectFinishTime"
          label="预计完成时间"
          rules={[{ required: true, message: '请选择预计完成时间' }]}
        >
          <DatePicker
            style={{ width: '100%' }}
            disabledDate={(date) =>
              Boolean(date && date.isBefore(moment().startOf('day')))
            }
          />
        </Form.Item>
        <Form.Item
          name="reason"
          label="反馈原因"
          extra={
            <span
              className={
                reasonLength > 500 ? styles.overLimit : styles.reasonCount
              }
            >
              {reasonLength}/500
            </span>
          }
          rules={[
            { required: true, message: '请输入反馈原因' },
            { max: 500, message: '反馈原因不能超过500字' },
          ]}
        >
          <Input.TextArea
            rows={4}
            onChange={(event) => setReasonLength(event.target.value.length)}
          />
        </Form.Item>
        <Form.Item label="凭证">
          <Upload
            fileList={fileList}
            accept=".png,.jpg,.jpeg,.pdf"
            multiple
            maxCount={10}
            beforeUpload={validateFile}
            customRequest={uploadFile}
            onChange={({ fileList: next }) => setFileList(next)}
            onPreview={(file) => {
              const uploaded = file.response as
                | OverdueFeedbackUploadedFile
                | undefined
              const url = `${uploaded?.baseUrl || ''}${uploaded?.url || file.url || ''}`
              if (url) window.open(url, '_blank', 'noopener,noreferrer')
            }}
          >
            <Button icon={<UploadOutlined />}>上传凭证</Button>
          </Upload>
        </Form.Item>
      </Form>
    </Modal>
  )
}
